import React from 'react';
import { Plus, X } from 'lucide-react';
import { TextField, Autocomplete } from '@anyemp/ui-kit';

/**
 * LinkForm — массив ссылок (tool из справочника + URL).
 * Структурно повторяет ContactForm, но без проверки уникальности и без "Verified".
 * Значение (value) трактуется как URL.
 */
const LinkForm = ({
  links = [],
  linkToolsOptions = [],
  onLinksChange,
  title = "Links",
  showTitle = true,
  className = "",
  validationErrors = {}, // Ошибки валидации
  onClearValidationError = () => {} // Callback для очистки ошибок валидации
}) => {
  const addLink = () => {
    const firstTool = linkToolsOptions.length > 0 ? linkToolsOptions[0] : null;
    const newLink = {
      tool_id: firstTool ? firstTool.id : null,
      tool_name: firstTool ? firstTool.name : null,
      value: ''
    };

    onLinksChange([...links, newLink]);
  };

  const removeLink = (index) => {
    onLinksChange(links.filter((_, i) => i !== index));
  };

  const updateLink = (index, field, value) => {
    const updatedLinks = links.map((link, i) => {
      if (i !== index) return link;
      const updated = { ...link, [field]: value };
      // При смене tool_id денормализуем имя выбранного tool в tool_name
      if (field === 'tool_id') {
        const selectedTool = linkToolsOptions.find(tool => tool.id === value);
        updated.tool_name = selectedTool ? selectedTool.name : null;
      }
      return updated;
    });

    onLinksChange(updatedLinks);

    // Clear validation error for the changed field
    if (field === 'tool_id') {
      onClearValidationError(`link_${index}_tool_id`);
    } else if (field === 'value') {
      onClearValidationError(`link_${index}_value`);
    }
  };

  return (
    <div className={className}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">{title}</h3>
          <button
            type="button"
            onClick={addLink}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Link
          </button>
        </div>
      )}

      {links.length === 0 ? (
        <div className="text-center py-8">
          <div className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.656-1.328a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
            </svg>
          </div>
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No links added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add Link" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link, index) => (
            <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">Link {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeLink(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Link Type *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={link.tool_id != null ? String(link.tool_id) : ''}
                      onChange={(value) => updateLink(index, 'tool_id', value ? Number(value) : null)}
                      placeholder="Select link type"
                      options={linkToolsOptions.map((tool) => ({
                        value: String(tool.id),
                        label: tool.name
                      }))}
                      error={!!validationErrors[`link_${index}_tool_id`]}
                    />
                    {validationErrors[`link_${index}_tool_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {validationErrors[`link_${index}_tool_id`]}
                      </p>
                    )}
                  </div>

                  <div>
                    <TextField
                      label="URL"
                      required
                      fullWidth
                      size="small"
                      type="text"
                      value={link.value || ''}
                      onChange={(e) => updateLink(index, 'value', e.target.value)}
                      placeholder="https://..."
                      error={!!validationErrors[`link_${index}_value`]}
                      helperText={validationErrors[`link_${index}_value`]}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkForm;
